import { isDefAndNotNull } from '@utils/def';
import { ReactNode } from 'react';
import { FieldErrors } from 'react-hook-form';
import { FieldError } from 'react-hook-form/dist/types/errors';

import { HedgingInstrumentInterestRateMode } from '../../models/HedgingInstrument/enum';
import { LegTypes } from '../../models/Leg/enum';
import { LegType } from '../../models/LegType';

import { ColorModes, PricerModes, TransactionReviewStatusModes } from './enums';
import { TransactionTypeModes } from './enums/TransactionTypeModes';
import {
  ValidationErrorText,
  ValidationErrorTitle,
  ValidationHeaderError,
} from './styles';
import {
  FormValidationRefType,
  FormValidationType,
  TableValidationRefType,
  TableValidationType,
} from './types';

export const TransactionReviewStatusColors: Record<
  TransactionReviewStatusModes,
  ColorModes
> = {
  [TransactionReviewStatusModes.PENDING]: ColorModes.warning,
  [TransactionReviewStatusModes.ACCEPTED]: ColorModes.success,
  [TransactionReviewStatusModes.AMEND]: ColorModes.warning,
  [TransactionReviewStatusModes.REFUSED]: ColorModes.error,
  [TransactionReviewStatusModes.PROBLEMATIC]: ColorModes.warning,
  [TransactionReviewStatusModes.REVIEWED]: ColorModes.success,
  [TransactionReviewStatusModes['PENDING CLOSE']]: ColorModes.orange,
};

export const TransactionReviewStatusLabelText: Record<
  TransactionReviewStatusModes,
  string | undefined
> = {
  [TransactionReviewStatusModes.PENDING]: 'Pending',
  [TransactionReviewStatusModes.ACCEPTED]: undefined,
  [TransactionReviewStatusModes.AMEND]: 'Amendment',
  [TransactionReviewStatusModes.REFUSED]: 'Rejected',
  [TransactionReviewStatusModes.PROBLEMATIC]: 'Rejected',
  [TransactionReviewStatusModes.REVIEWED]: undefined,
  [TransactionReviewStatusModes['PENDING CLOSE']]: 'Pending Close',
};

export const TransactionTypeColors: Record<TransactionTypeModes, ColorModes> = {
  [TransactionTypeModes.Actual]: ColorModes.success,
  [TransactionTypeModes.Hypothetical]: ColorModes.violet,
  [TransactionTypeModes.Test]: ColorModes.gray,
  [TransactionTypeModes.Valuation]: ColorModes.gray,
  [TransactionTypeModes.Deleted]: ColorModes.error,
  [TransactionTypeModes.Internal]: ColorModes.blue,
  [TransactionTypeModes.Component]: ColorModes.pinkGraph,
  [TransactionTypeModes.TradeRequest]: ColorModes.gray,
};

export const TransactionTypeLabelText: Record<
  TransactionTypeModes,
  string | undefined
> = {
  [TransactionTypeModes.Actual]: 'Live',
  [TransactionTypeModes.Hypothetical]: 'Hypo',
  [TransactionTypeModes.Test]: undefined,
  [TransactionTypeModes.Valuation]: 'Draft',
  [TransactionTypeModes.Deleted]: 'Deleted',
  [TransactionTypeModes.Internal]: 'Draft',
  [TransactionTypeModes.Component]: 'Component',
  [TransactionTypeModes.TradeRequest]: undefined,
};

export const AddLegsForPricerList: Record<PricerModes, LegTypes[]> = {
  [PricerModes.interestRate]: [
    LegTypes.fixed,
    LegTypes.float,
    LegTypes.cap,
    LegTypes.floor,
    LegTypes.scheduledPayments,
  ],
  [PricerModes.fx]: [],
  [PricerModes.commodity]: [
    LegTypes.scheduledPayments,
    LegTypes.commodityFixed,
    LegTypes.commodityFloat,
    LegTypes.commodityCall,
    LegTypes.commodityPut,
  ],
  [PricerModes.inflation]: [
    LegTypes.fixed,
    LegTypes.float,
    LegTypes.inflation,
    LegTypes.compoundingFixed,
    LegTypes.payGo,
  ],
  [PricerModes.debt]: [],
  [PricerModes.custom]: [
    LegTypes.fixed,
    LegTypes.float,
    LegTypes.cap,
    LegTypes.floor,
    LegTypes.inflation,
    LegTypes.forward,
    LegTypes.scheduledPayments,
    LegTypes.commodityFixed,
    LegTypes.payGo,
    LegTypes.windowedForward,
    LegTypes.upfrontPremium,
    LegTypes.commodityFixed,
    LegTypes.commodityFloat,
    LegTypes.commodityCall,
    LegTypes.commodityPut,
  ],
};

export const getTransactionTypeName = (
  legTypesList: LegType[],
  legTypeId: LegTypes,
) => legTypesList.find((leg) => leg.leg_type_id === legTypeId)?.type;

export const getCurrentNotionalValueForLeg = (
  instrumentType: HedgingInstrumentInterestRateMode,
  legTypeId: LegTypes,
  legNumber: number,
): boolean => {
  switch (instrumentType) {
    case HedgingInstrumentInterestRateMode.irSwap:
    case HedgingInstrumentInterestRateMode.IrSwapWithEmbeddedFloor:
      return legTypeId === LegTypes.float;
    case HedgingInstrumentInterestRateMode.irCap:
      return legTypeId === LegTypes.cap;
    case HedgingInstrumentInterestRateMode.irFloor:
      return legTypeId === LegTypes.floor;
    case HedgingInstrumentInterestRateMode.crossCurrencySwap:
      return legTypeId === LegTypes.fixed;
    case HedgingInstrumentInterestRateMode.irCollar:
    case HedgingInstrumentInterestRateMode.irCapWithFixed:
    case HedgingInstrumentInterestRateMode.irCollarWithPremium:
      return legTypeId === LegTypes.cap;
    case HedgingInstrumentInterestRateMode.fxForward:
    case HedgingInstrumentInterestRateMode.fxSwap:
    case HedgingInstrumentInterestRateMode.fxForwardNDF:
    case HedgingInstrumentInterestRateMode.fxOption:
    case HedgingInstrumentInterestRateMode.fxAveragingForward:
    case HedgingInstrumentInterestRateMode.fxForwardStrip:
      return legTypeId === LegTypes.scheduledPayments && legNumber === 0;
    case HedgingInstrumentInterestRateMode.inflationSwap:
      return legTypeId === LegTypes.inflation;
    case HedgingInstrumentInterestRateMode.inflationWitPayGo:
      return legTypeId === LegTypes.fixed;
    case HedgingInstrumentInterestRateMode.commoditySwap:
      return legTypeId === LegTypes.commodityFixed;
    case HedgingInstrumentInterestRateMode.commodityCollar:
    case HedgingInstrumentInterestRateMode.commodityCall:
      return legTypeId === LegTypes.commodityCall;
    case HedgingInstrumentInterestRateMode.commodityPut:
      return legTypeId === LegTypes.commodityPut;
    case HedgingInstrumentInterestRateMode.customTrade:
      return legTypeId === LegTypes.float;
    default:
      return false;
  }
};

export const getValidationFormsResult = (
  validationFormFunctions: FormValidationRefType,
) => {
  const errorList = Object.values(validationFormFunctions).reduce(
    (acc: ReactNode[], validation: FormValidationType) => {
      const { formState } = validation;
      const errors: FieldErrors = formState.errors;

      if (Object.keys(errors).length) {
        const errorsHtml = Object.values(errors).reduce(
          (errorAcc: ReactNode[], error) => {
            const fieldError = error as FieldError;

            errorAcc.push(
              <ValidationErrorText>
                <ValidationErrorTitle>
                  {fieldError?.ref?.name}
                </ValidationErrorTitle>
                : {fieldError.message}
              </ValidationErrorText>,
            );
            return errorAcc;
          },
          [],
        );

        acc.push(
          <ValidationHeaderError>{validation.formName}:</ValidationHeaderError>,
        );
        acc.push(...errorsHtml);
      }
      return acc;
    },
    [],
  );

  return { success: !errorList.length, errorList };
};
export const getValidationTableResult = (
  validationFlowsFunctions: TableValidationRefType,
) => {
  const errorList = Object.values(validationFlowsFunctions).reduce(
    (acc: ReactNode[], validation: TableValidationType) => {
      const { validationErrorState } = validation;

      if (
        isDefAndNotNull(validationErrorState) &&
        Object.keys(validationErrorState).length
      ) {
        const errorsHtml = Object.entries(validationErrorState).reduce(
          (errorAcc: ReactNode[], rowError) => {
            errorAcc.push(
              <ValidationErrorText>
                Row #{rowError[0]}:{' '}
                <ValidationErrorTitle>
                  {Object.keys(rowError[1]).join(', ')}
                </ValidationErrorTitle>{' '}
                - Fill required fields
              </ValidationErrorText>,
            );
            return errorAcc;
          },
          [],
        );

        acc.push(
          <ValidationHeaderError>
            {validation.tableName}:
          </ValidationHeaderError>,
        );
        acc.push(...errorsHtml);
      }
      return acc;
    },
    [],
  );
  const warningList = Object.values(validationFlowsFunctions).reduce(
    (acc: ReactNode[], validation: TableValidationType) => {
      const { validationWarningState } = validation;

      if (
        isDefAndNotNull(validationWarningState) &&
        Object.keys(validationWarningState).length
      ) {
        const errorsHtml = Object.entries(validationWarningState).reduce(
          (errorAcc: ReactNode[], rowError) => {
            errorAcc.push(
              <ValidationErrorText>
                Row #{rowError[0]}:{' '}
                {Object.values(rowError[1]).map((error, index) => (
                  <ValidationErrorText key={index}>
                    <ValidationErrorTitle>
                      {error?.columnName}
                    </ValidationErrorTitle>
                    : {error?.message}
                  </ValidationErrorText>
                ))}
              </ValidationErrorText>,
            );
            return errorAcc;
          },
          [],
        );

        acc.push(
          <ValidationHeaderError>
            {validation.tableName}:
          </ValidationHeaderError>,
        );
        acc.push(...errorsHtml);
      }
      return acc;
    },
    [],
  );

  return { success: !errorList.length, errorList, warningList };
};
